import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Shield, Award, Users, Globe, Phone,
  Mail, MapPin, FileText, CheckCircle, ExternalLink, Briefcase,
  Download, Server, Brain, Target, BarChart3, Loader2, Cpu,
  Lock, ClipboardCheck
} from "lucide-react";
import { usePageSEO } from "@/hooks/usePageSEO";

const API = process.env.REACT_APP_BACKEND_URL;

const NAICS_CODES = [
  { code: "541611", desc: "Administrative & General Management Consulting Services", primary: true },
  { code: "541618", desc: "Other Management Consulting Services" },
  { code: "541512", desc: "Computer Systems Design Services" },
  { code: "541511", desc: "Custom Computer Programming Services" },
  { code: "541519", desc: "Other Computer Related Services" },
  { code: "518210", desc: "Data Processing, Hosting, and Related Services" },
  { code: "541690", desc: "Other Scientific and Technical Consulting Services" },
  { code: "611430", desc: "Professional and Management Development Training" },
  { code: "561320", desc: "Temporary Help Services" },
  { code: "561110", desc: "Office Administrative Services" },
  { code: "541810", desc: "Advertising Agencies" },
];

const CORE_CAPABILITIES = [
  {
    title: "Management & Strategy Consulting",
    icon: Briefcase,
    items: ["Strategic planning and execution frameworks", "Organizational assessments and restructuring", "Change management and program support", "Performance evaluation and improvement"]
  },
  {
    title: "Operational Modernization",
    icon: Server,
    items: ["Business process reengineering", "Workflow automation and optimization", "SOP development and internal controls", "Organizational design and scalability planning"]
  },
  {
    title: "AI-Powered Program Delivery",
    icon: Brain,
    items: ["AI-driven business and program assessments", "Automated 30-90 day action planning", "Performance forecasting and insights", "Decision-support systems for agencies and grantees"]
  },
  {
    title: "Performance Management & Reporting",
    icon: BarChart3,
    items: ["KPI framework development", "Real-time dashboards and analytics", "Outcome tracking and impact reporting", "Grant and program accountability systems"]
  },
  {
    title: "Technology & Digital Transformation",
    icon: Cpu,
    items: ["Systems modernization and integration strategy", "AI enablement and automation solutions", "Custom platform development (FastAPI, React, MongoDB)", "Secure cloud-based infrastructure and compliance controls"]
  },
  {
    title: "Workforce & Program Support",
    icon: Users,
    items: ["Workforce development program support", "Remote staffing and operational support", "Training and capacity-building systems", "Entrepreneur and small business program delivery"]
  },
];

const DIFFERENTIATORS = [
  "AI-powered, end-to-end consulting and program delivery platform",
  "Consultant-led strategy enhanced by automation and real-time data",
  "Built-in grant tracking, cohort management, and impact reporting tools",
  "Co-branded partner portals for agencies, CDFIs, and economic development organizations",
  "Scalable solutions supporting entire client lifecycle — from intake to measurable outcomes",
  "Enterprise-grade security (AES-256 encryption, audit logging, access controls)",
  "Agile, cost-effective, and micro-purchase ready",
];

const METHODOLOGIES = [
  { name: "R.A.P.I.D.™ Business Recalibration System", desc: "5-session structured program for rapid business acceleration" },
  { name: "AI Business Clarity Assessment Framework", desc: "AI-powered diagnostic identifying bottlenecks and growth opportunities" },
  { name: "90-Day Operational Growth System", desc: "Step-by-step execution roadmap with measurable milestones" },
  { name: "Business Solutions Center (BSC) Platform", desc: "End-to-end ecosystem for diagnostics, planning, execution, and reporting" },
];

const CONTRACTING_READINESS = [
  "Active SAM Registration",
  "UEI & CAGE Active",
  "Micro-Purchase Capable",
  "Grant & Cooperative Agreement Support Ready",
  "Subcontracting & Teaming Ready",
  "Government-Compliant Systems and Reporting",
];

const TECH_STACK = [
  "Python 3.11 (FastAPI)",
  "React 18 Front-End Architecture",
  "MongoDB Database",
  "OpenAI GPT Integration",
  "AES-256 Encryption",
  "Audit Logging & Role-Based Access Controls",
];

export const CapabilityStatement = () => {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  usePageSEO("Capability Statement", "My Biz Consulting capability statement. AI-powered strategic consulting for federal, state, and local agencies. NAICS 541611. Woman-owned, minority-owned small business.");

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const link = document.createElement('a');
      link.href = '/docs/My_Biz_Consulting_Capability_Statement.pdf';
      link.download = 'My_Biz_Consulting_Capability_Statement.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Capability Statement downloading!");
    } catch (err) {
      // Fallback: open in new tab
      window.open('/docs/My_Biz_Consulting_Capability_Statement.pdf', '_blank');
      toast.success("Capability Statement opened!");
    }
    setDownloading(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="capability-statement-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" />
            <span className="text-sm text-neutral-400">Capability Statement</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloading}
              className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10" data-testid="download-capstat-pdf">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Download PDF
            </Button>
            <Button variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#333333] to-neutral-800 py-14 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <Badge className="bg-[#39FF14] text-black mb-4">Capability Statement</Badge>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">My Biz Consulting LLC</h1>
              <p className="text-neutral-300 text-base max-w-2xl leading-relaxed">
                AI-powered strategic consulting and business solutions firm delivering management consulting,
                digital transformation, and performance optimization services to federal, state, and local agencies.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="outline" className="border-[#39FF14]/40 text-[#39FF14]">Woman-Owned</Badge>
                <Badge variant="outline" className="border-[#39FF14]/40 text-[#39FF14]">Minority-Owned</Badge>
                <Badge variant="outline" className="border-[#39FF14]/40 text-[#39FF14]">Family-Owned</Badge>
                <Badge variant="outline" className="border-[#39FF14]/40 text-[#39FF14]">Small Business</Badge>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 text-sm space-y-2 min-w-[280px]">
              <div className="flex items-center gap-2 text-neutral-300"><MapPin className="w-4 h-4 text-[#39FF14]" /> 304 Baltimore Avenue, Folcroft, PA 19032</div>
              <div className="flex items-center gap-2 text-neutral-300"><Phone className="w-4 h-4 text-[#39FF14]" /> 215-443-1076</div>
              <div className="flex items-center gap-2 text-neutral-300"><Mail className="w-4 h-4 text-[#39FF14]" /> Jamilah@mybizconsulting.net</div>
              <div className="flex items-center gap-2 text-neutral-300"><Globe className="w-4 h-4 text-[#39FF14]" /> www.mybizconsulting.net</div>
              <div className="border-t border-white/10 pt-2 mt-2 space-y-1 text-xs text-neutral-400">
                <p><strong className="text-neutral-300">DUNS:</strong> 961656324</p>
                <p><strong className="text-neutral-300">CAGE:</strong> 7EWD3</p>
                <p><strong className="text-neutral-300">UEI:</strong> HKZLM5CFRHS3</p>
                <p><strong className="text-neutral-300">TIN:</strong> 27-1883915</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-10">
        {/* Company Overview */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#2BC40E]" /> Company Overview
          </h2>
          <div className="bg-neutral-50 rounded-xl p-5 border border-neutral-200 text-sm text-neutral-700 leading-relaxed space-y-3">
            <p>
              My Biz Consulting LLC is an AI-powered strategic consulting and business solutions firm delivering
              management consulting, digital transformation, and performance optimization services to federal,
              state, and local agencies.
            </p>
            <p>
              Through our proprietary Business Solutions Center (BSC) platform, we combine consultant-led strategy
              with advanced AI tools to support program delivery, workforce development, and operational modernization.
              Our solutions enable agencies and funded programs to improve efficiency, ensure accountability, and
              achieve measurable outcomes through data-driven insights, automated workflows, and real-time performance tracking.
            </p>
            <p className="text-neutral-500 text-xs">
              We support micro-purchases, simplified acquisitions, task orders, grants, cooperative agreements,
              and subcontracting opportunities.
            </p>
          </div>
        </section>

        {/* NAICS Codes */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2BC40E]" /> NAICS Codes
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {NAICS_CODES.map((n) => (
              <div key={n.code} className={`p-3 rounded-lg border ${n.primary ? "bg-[#39FF14]/5 border-[#39FF14]/30" : "bg-neutral-50 border-neutral-100"}`}>
                <span className={`font-mono text-sm font-bold ${n.primary ? "text-[#2BC40E]" : "text-neutral-700"}`}>{n.code}</span>
                {n.primary && <Badge className="ml-2 bg-[#39FF14] text-black text-[9px]">Primary</Badge>}
                <p className="text-[11px] text-neutral-600 mt-1">{n.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Core Capabilities */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#2BC40E]" /> Core Capabilities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CORE_CAPABILITIES.map((cap) => (
              <Card key={cap.title} className="border-neutral-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 bg-[#39FF14]/10 rounded-lg flex items-center justify-center">
                      <cap.icon className="w-4.5 h-4.5 text-[#2BC40E]" />
                    </div>
                    <h3 className="font-semibold text-sm text-neutral-900">{cap.title}</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {cap.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-neutral-600">
                        <CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5 flex-shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Proprietary Systems */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#2BC40E]" /> Proprietary Systems & Methodologies
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {METHODOLOGIES.map((m) => (
              <div key={m.name} className="p-4 bg-[#333333] rounded-xl text-white">
                <p className="font-semibold text-sm mb-1">{m.name}</p>
                <p className="text-xs text-neutral-400">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Differentiators */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#2BC40E]" /> Differentiators
          </h2>
          <div className="bg-gradient-to-r from-[#39FF14]/5 to-emerald-50 rounded-xl p-5 border border-[#39FF14]/20">
            <ul className="space-y-2">
              {DIFFERENTIATORS.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircle className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />{d}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Tech Stack + Contracting Readiness side by side */}
        <div className="grid md:grid-cols-2 gap-6">
          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-[#2BC40E]" /> Technology Infrastructure
            </h2>
            <div className="space-y-2">
              {TECH_STACK.map((t, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 bg-neutral-50 rounded-lg border border-neutral-100 text-sm text-neutral-700">
                  <Lock className="w-3.5 h-3.5 text-neutral-400" />{t}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#2BC40E]" /> Contracting Readiness
            </h2>
            <div className="space-y-2">
              {CONTRACTING_READINESS.map((c, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 bg-green-50 rounded-lg border border-green-100 text-sm text-green-800">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />{c}
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Past Performance */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#2BC40E]" /> Past Performance Experience
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {["Operational transformation and systems modernization", "Workforce and small business program support",
              "CRM and digital platform implementation", "KPI system design and performance tracking",
              "Strategic planning and process improvement initiatives"].map((p, i) => (
              <div key={i} className="p-3 bg-neutral-50 rounded-lg border border-neutral-100 text-sm text-neutral-700 flex items-start gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 flex-shrink-0" />{p}
              </div>
            ))}
          </div>
        </section>

        {/* Government Keywords */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3">Government Keywords</h2>
          <div className="flex flex-wrap gap-1.5">
            {["Management Consulting", "Program Management", "Operational Improvement", "Organizational Assessment",
              "Digital Transformation", "AI Integration", "Process Improvement", "KPI Development",
              "Performance Metrics", "Workforce Development", "IT Modernization", "Data Analytics",
              "Workflow Automation", "Internal Controls", "Cloud Solutions", "Grant Management", "Program Evaluation"].map((kw) => (
              <Badge key={kw} variant="outline" className="border-neutral-200 text-neutral-600 text-xs py-1">{kw}</Badge>
            ))}
          </div>
        </section>

        {/* Leadership */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2BC40E]" /> Leadership
          </h2>
          <Card className="border-neutral-200">
            <CardContent className="p-5">
              <h3 className="font-semibold text-neutral-900 text-lg mb-1">Jamilah Lawry</h3>
              <p className="text-sm text-[#2BC40E] font-medium mb-3">Founder & Managing Consultant</p>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Technology strategist and systems architect with deep expertise across AI, cloud platforms,
                network infrastructure, digital marketing technology, and workflow design. Certifications
                include Google Cloud & NVIDIA, Apple Search Ads, AI Marketing, CompTIA, Microsoft, and
                Project Management.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Ecosystem Links */}
        <section>
          <h2 className="text-xl font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#2BC40E]" /> Our Ecosystem
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { href: "https://mybizconsultingllc.anytimemailbox.com/", label: "Anytime Mailbox", desc: "Virtual mailbox & business services" },
              { href: "https://indastreetsradio.com/", label: "In Da Streets Radio", desc: "Internet radio & digital media" },
              { href: "https://www.inspiredastreets.org/", label: "Inspire Da Streets", desc: "Nonprofit — mental health & creative communities" },
            ].map(item => (
              <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer"
                className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 hover:border-[#39FF14]/50 transition-all group">
                <h3 className="font-semibold text-neutral-900 text-sm flex items-center gap-1 group-hover:text-[#2BC40E]">
                  {item.label} <ExternalLink className="w-3 h-3 opacity-40" />
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">{item.desc}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Download CTA */}
        <div className="text-center py-6">
          <Button onClick={handleDownloadPDF} disabled={downloading} size="lg"
            className="bg-[#333333] hover:bg-[#444444] text-white h-12 px-8">
            {downloading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />}
            Download Capability Statement PDF
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CapabilityStatement;
